import { Observable, observable, throwError } from 'rxjs';
import {
  map,
  catchError,
  share,
  concatMap,
  finalize
} from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Dictionary } from '@common_libraries/helpers/Dictionary';
import { LocalStorageService, SessionStorageService } from 'ngx-webstorage';
import { Router, RouterStateSnapshot } from '@angular/router';

// Load environment setting
import { environment } from '@environments/environment';
import { SessionService } from '@services/session/session.service';
import { LogoutReasons } from '@common_libraries/lib/Enums';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { from } from 'rxjs';
import { TokenParams } from '@okta/okta-auth-js';

enum ContentType {
  JSON = 0,
  FORM = 1,
  TEXT = 2,
}

export class ServiceReturn {
  public status: number;
  public json: any;
}

export class RequestOptions {
  method: string;
  url: string;
  headers: HttpHeaders;
  body: string;
  withCredentials: boolean;
  contentType: ContentType;
}

@Injectable({providedIn: 'root'})
export class ServiceLibrary {
  public static ContentType = ContentType;
  // Internal API Proxy
  private _webApiUrl = environment.webApiUrl;
  private reAuthObs;
  public oktaAuth;
  public reAuthCount: number = 0;
  public reAuthLimit: number = 2;
  constructor(
    private http: HttpClient,
    private localSt: LocalStorageService,
    private router: Router,
    private sessionService: SessionService,
    private slimLoader: LoadingBarService,
  ) {}

  private PerformServiceCall(
    requestMethod: string,
    apiRequest: string,
    contentType: ContentType,
    parameters: Dictionary,
    withCredentials: boolean,
    authObjectID?: any,
    showLoader: boolean = true,
    responseType: string = 'json'
  ): any {
    if (showLoader) {
      this.slimLoader.start();
    }

    const requestOptions = this.CreateRequestOptions(
      requestMethod,
      contentType,
      parameters,
      apiRequest,
      withCredentials,
      authObjectID
    );

    const Options: any = {
      withCredentials: requestOptions.withCredentials,
      body: requestOptions.body,
      headers: requestOptions.headers,
      observe: 'response',
    };

    switch (responseType) {
      case 'json':
        Options.responseType = 'json';
        break;
      case 'blob':
        Options.responseType = 'blob';
        break;
      case 'arraybuffer':
        Options.responseType = 'arraybuffer';
        break;
      case 'text':
        Options.responseType = 'text';
        break;
      default:
        Options.responseType = 'json';
    }

    const serviceCall = this.http
      .request(requestMethod, requestOptions.url, Options)
      .pipe(
        map((res: any) => {
          if(requestOptions.withCredentials && requestOptions.url.indexOf('api/Session/Set') < 0) {
            this.reAuthCount = 0;
          }
          if (res) {
            const responseContentType = res.headers.get('content-type');
            if (
              responseContentType &&
              responseContentType.indexOf('application/json') > -1
            ) {
              const serviceReturn = new ServiceReturn();
              serviceReturn.status = res.status;
              serviceReturn.json = res.body;
              return serviceReturn;
            } else {
              return res;
            }
          }
        }),
        catchError((error, source) => {
          return this.HandleErrorResponse(error, source);
        }),
        finalize(() => {
          if (showLoader) {
            this.slimLoader.complete();
          }
        })
      );

    return serviceCall;
  }

  private HandleErrorResponse(error: any, source: any) {
    if (error.status === 500) {
      if (error instanceof Response) {
        return throwError({ status: error.status, json: error.json() });
      } else {
        return throwError(error);
      }
//      return throwError(new Error(error.status));
    } else if (error.status === 404) {
      return throwError(new Error(error.status));
    } else if (error.status === 400) {
      if (error instanceof Response) {
        return throwError({ status: error.status, json: error.json() });
      } else {
        return throwError(error);
      }
    } else if (error.status === 560) {
      this.sessionService.SetIsLoggedIn(false);
      window.location.href = '/login/' + LogoutReasons.SecurityModule;
    } else if (error.status === 561) {
      this.sessionService.SetIsLoggedIn(false);
      window.location.href = '/login/' + LogoutReasons.HasPermission;
    } else if (error.status === 401) {
      
      // User is unauthorized
      if (this.reAuthObs === undefined) {
        this.ReAuth(false);
      } else {
      }
      return this.reAuthObs.pipe(
        concatMap(() => {
          this.reAuthObs = undefined;
          return source;
        }));
      
    } else {
      if (error instanceof Response) {
        return throwError({ status: error.status, json: error.json() });
      } else {
        return throwError(error);
      }
    }
  }

  private CreateRequestOptions(
    requestMethod: string,
    contentType: ContentType,
    parameters: Dictionary,
    apiRequest: string,
    withCredentials: boolean,
    authObjectID?: any
  ) {
    const headers = this.BuildHeaders(contentType, authObjectID);
    const body = this.ParseParameters(parameters, contentType);
    const requestOptions = new RequestOptions();
    requestOptions.method = requestMethod;
    requestOptions.url = this._webApiUrl + apiRequest;
    requestOptions.body = body;
    requestOptions.withCredentials = withCredentials;
    requestOptions.headers = headers;
    requestOptions.contentType = contentType;

    return requestOptions;
  }

  private BuildHeaders(type: ContentType, authObjectID?: any) {
    let contentType;
    switch (type) {
      case ContentType.JSON:
        contentType = 'application/json; charset=utf-8';
        break;
      case ContentType.FORM:
        contentType = 'application/x-www-form-urlencoded';
        break;
      case ContentType.TEXT:
        contentType = 'text/plain';
        break;
      default:
        contentType = 'application/json; charset=utf-8';
    }

    let headers = new HttpHeaders();
    headers = headers.set('Content-Type', contentType);

    if (authObjectID) {
      headers = headers.set('AuthObjectID', String(authObjectID));
    }

    headers = headers.set(
      'TigerEyeCompanyID',
      this.sessionService.GetSelectedCompanyID().toString()
    );
    return headers;
  }

  private ParseParameters(parameters: Dictionary, contentType: ContentType) {
    switch (contentType) {
      case ContentType.JSON:
        return this.BuildJsonParameters(parameters);
      case ContentType.FORM:
        return this.BuildFormParameters(parameters);
      default:
        return this.BuildJsonParameters(parameters);
    }
  }

  private BuildFormParameters(parameters: Dictionary) {
    const urlSearchParams = new URLSearchParams();
    for (const paramKey of parameters.keys()) {
      urlSearchParams.append(paramKey, parameters[paramKey]);
    }

    return urlSearchParams.toString();
  }

  private BuildJsonParameters(parameters: Dictionary) {
    const jsonObj = new Object();
    for (const paramKey of parameters.keys()) {
      jsonObj[paramKey] = parameters[paramKey];
    }

    return JSON.stringify(jsonObj);
  }

  HttpGet(
    apiRequest: string,
    contentType: ContentType,
    parameters: Dictionary,
    authObjectID?: number,
    withCredentials: boolean = true
  ) {
    return this.PerformServiceCall(
      'get',
      apiRequest,
      contentType,
      parameters,
      withCredentials,
      authObjectID,
      true,
      'json'
    );
  }

  HttpGetPolling(
    apiRequest: string,
    contentType: ContentType,
    parameters: Dictionary,
    authObjectID?: number,
    withCredentials: boolean = true
  ) {
    return this.PerformServiceCall(
      'get',
      apiRequest,
      contentType,
      parameters,
      withCredentials,
      authObjectID,
      false,
      'json'
    );
  }

  HttpPost(
    apiRequest: string,
    contentType: ContentType,
    parameters: Dictionary,
    authObjectID?: any,
    withCredentials: boolean = true
  ) {
    return this.PerformServiceCall(
      'post',
      apiRequest,
      contentType,
      parameters,
      withCredentials,
      authObjectID,
      true,
      'json'
    );
  }

  HttpPostPolling(
    apiRequest: string,
    contentType: ContentType,
    parameters: Dictionary,
    authObjectID?: any,
    withCredentials: boolean = true
  ) {
    return this.PerformServiceCall(
      'post',
      apiRequest,
      contentType,
      parameters,
      withCredentials,
      authObjectID,
      false,
      'json'
    );
  }

  HttpGetWithResponseType(
    apiRequest: string,
    contentType: ContentType,
    parameters: Dictionary,
    authObjectID?: any,
    withCredentials: boolean = true,
    responseType: string = 'json'
  ) {
    return this.PerformServiceCall(
      'get',
      apiRequest,
      contentType,
      parameters,
      withCredentials,
      authObjectID,
      true,
      responseType
    );
  }

  HttpPostWithResponseType(
    apiRequest: string,
    contentType: ContentType,
    parameters: Dictionary,
    authObjectID?: any,
    withCredentials: boolean = true,
    responseType: string = 'json'
  ) {
    return this.PerformServiceCall(
      'post',
      apiRequest,
      contentType,
      parameters,
      withCredentials,
      authObjectID,
      true,
      responseType
    );
  }

  HttpDelete(
    apiRequest: string,
    contentType: ContentType,
    parameters: Dictionary,
    authObjectID?: number,
    withCredentials: boolean = true
  ) {
    return this.PerformServiceCall(
      'delete',
      apiRequest,
      contentType,
      parameters,
      withCredentials,
      authObjectID,
      true,
      'json'
    );
  }

   async ReAuth(ignoreReAuth: boolean = true) {
    if(this.oktaAuth !== undefined && this.reAuthCount<= this.reAuthLimit) {    
      this.SetupRedirectURL();
      let authState = this.oktaAuth.authStateManager.getAuthState();
      if (authState !== undefined && authState !== null &&  authState.isAuthenticated) { 
        const tokenParams: TokenParams = {
          scopes: ['openid', 'email', 'profile'],
        }
      this.reAuthCount++;
      return (this.reAuthObs = from(this.oktaAuth.tokenManager.renew('accessToken'))
        .pipe(
          map((res: any) => {
            if (res) {
              return { status: res.status, body: res.body };
            }
          }),
          catchError((error, source) => {
            return this.FailAndLogout();
          }),
          share()
        ));

      } else {
        return this.FailAndLogout();
      }
    }  else {
      return this.FailAndLogout();
    }
  }

  SetupRedirectURL() {
    const redURL = this.localSt.retrieve('redirect_url');
        if (redURL == null) {
          const snapshot: RouterStateSnapshot = this.router.routerState
            .snapshot;

          this.localSt.store('redirect_url_create', Date.now());

          let goToURL = snapshot.url;
          if (goToURL === '') {
            goToURL = window.location.pathname;
          }
          this.localSt.store('redirect_url', goToURL);
        }
  }

  FailAndLogout() {
        this.slimLoader.complete();
        this.sessionService.SetIsLoggedIn(false);
        this.reAuthCount = 0;
        this.SetupRedirectURL();
        // using window.location instead of router because reauth failed -> you are going back to login whether you like it or not
        window.location.href = '/login';
        return new Observable();
  }

  Login(username: string, password: string): any {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });

    const urlSearchParams = new URLSearchParams();
    urlSearchParams.append('username', username);
    urlSearchParams.append('password', password);
    urlSearchParams.append('grant_type', 'password');
    urlSearchParams.append('client_id', environment.clientId);
    const body = urlSearchParams.toString();

    const requestOptions = new RequestOptions();
    requestOptions.method = 'post';
    requestOptions.url = this._webApiUrl + '/token';
    requestOptions.headers = headers;
    requestOptions.withCredentials = true;
    requestOptions.body = body;

    return this.http
      .request(requestOptions.method, requestOptions.url, {
        body: requestOptions.body,
        withCredentials: requestOptions.withCredentials,
        headers: headers,
        observe: 'response',
        responseType: 'text',
      })
      .pipe(
        map((res: any) => {
          if (res) {
            return { status: res.status, body: res.body };
          }
        })
      );
  }

  GenerateDownload(Url: string): Observable<Object[]> {
    return Observable.create((observer) => {
      const slimLoader = this.slimLoader;
      slimLoader.start();
      const xhr = new XMLHttpRequest();

      xhr.open('POST', this._webApiUrl + Url, true);
      xhr.setRequestHeader('Content-type', 'application/json');
      xhr.setRequestHeader(
        'TigerEyeCompanyID',
        this.sessionService.GetSelectedCompanyID().toString()
      );
      xhr.withCredentials = true;
      xhr.responseType = 'blob';

      xhr.onreadystatechange = function () {
        slimLoader.complete();
        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            const contentType = this.getResponseHeader('content-type');
            const blob = new Blob([xhr.response], { type: contentType });
            observer.next(blob);
            observer.complete();
          } else {
            observer.error(xhr.response);
          }
        }
      };
      xhr.send();
    });
  }
}
